import { WireTypes } from '@flakiness/server/common/wireTypes.js';
import { consume, ContextType } from '@lit/context';
import { SlDropdown } from '@shoelace-style/shoelace';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { contexts } from '../contexts.js';
import { linkStyles } from './cssstyles.js';
import { timezoneLabel } from './timezone-selector.js';

/**
 * The history types to pick from. The server accepts any time period, but a bar longer than a
 * week mostly says that the test failed at some point in it.
 */
export const OFFERED_HISTORY_TYPES: WireTypes.HistoryType[] = ['commit', '3h', 'day', 'usweek'];

/** How each history type reads as an option. */
export const HISTORY_TYPE_LABELS: Record<WireTypes.HistoryType, string> = {
  'commit': 'By commit',
  '3h': 'By 3 hours',
  'day': 'By day',
  'usweek': 'By week',
  'monthweek': 'By week of month',
  'month': 'By month',
  'quarter': 'By quarter',
};

// The title of the history column.
const HISTORY_TYPE_TITLES: Record<WireTypes.HistoryType, string> = {
  'commit': 'History',
  '3h': '3-hour history',
  'day': 'Daily history',
  'usweek': 'Weekly history',
  'monthweek': 'Month-week history',
  'month': 'Monthly history',
  'quarter': 'Quarterly history',
};

// What one bar covers, for the help card of the period history types.
const HISTORY_PERIOD_NOUNS: Record<Exclude<WireTypes.HistoryType, 'commit'>, string> = {
  '3h': '3-hour window',
  'day': 'day',
  'usweek': 'week',
  'monthweek': 'week',
  'month': 'month',
  'quarter': 'quarter',
};

function renderHistoryHelp(historyType: WireTypes.HistoryType, timezone: string) {
  if (historyType === 'commit') {
    return html`
      <p>Each bar is one commit that ran the test, newest on the right.</p>
      <p><strong>Color:</strong> the test's result in that commit.</p>
      <p><strong>Height:</strong> the test's duration in that commit.</p>
    `;
  }
  const noun = HISTORY_PERIOD_NOUNS[historyType];
  return html`
    <p>Each bar is one ${noun} in which the test ran, newest on the right.</p>
    <p><strong>Color:</strong> the worst result the test had during that ${noun}. A single failure marks the whole ${noun} as failed.</p>
    <p><strong>Height:</strong> the test's duration in the latest commit of the ${noun} that ran it.</p>
    <p><strong>Timezone:</strong> ${timezoneLabel(timezone)}</p>
  `;
}

// Dispatched when the user picks a history type. `historyType` is undefined when
// "Use project default" is chosen (the local override should be cleared).
export class FKReportHistorySelectEvent extends Event {
  static readonly eventName = 'fk-history-change';
  readonly historyType?: WireTypes.HistoryType;

  constructor(historyType?: WireTypes.HistoryType) {
    super(FKReportHistorySelectEvent.eventName, {
      bubbles: true,
      composed: true,
      cancelable: false,
    });
    this.historyType = historyType;
  }
}

@customElement('report-history-selector')
export class ReportHistorySelector extends LitElement {
  @consume({ context: contexts.project, subscribe: true }) private _project?: ContextType<typeof contexts.project>;
  @consume({ context: contexts.router, subscribe: true }) private _router?: ContextType<typeof contexts.router>;

  // The local override for this view. Undefined => follow the project default.
  @property({ attribute: false }) historyType?: WireTypes.HistoryType;
  // Optional branch/PR label rendered next to the title (history is anchored to it).
  @property({ attribute: false }) branchName?: string;

  @query('sl-dropdown') private _dropdown?: SlDropdown;

  render() {
    const project = this._project;
    if (!project)
      return nothing;

    const effective = this.historyType ?? project.defaultHistoryType ?? 'commit';
    const title = HISTORY_TYPE_TITLES[effective];

    const option = (label: string, value: WireTypes.HistoryType | undefined, active: boolean) => html`
      <button class=history-option @click=${() => this._select(value)}>
        <sl-icon name=${active ? 'check' : nothing}></sl-icon>
        <span>${label}</span>
      </button>
    `;

    return html`
      <sl-dropdown placement=bottom-start hoist distance=10>
        <button slot=trigger class=history-trigger>
          ${this.branchName ? html`<branch-label .name=${this.branchName} ?icon=${true}></branch-label>` : nothing}
          <span>${title}</span>
          <sl-icon name=chevron-down></sl-icon>
        </button>
        <v-box class=history-picker>
          <section class=history-options>
            ${OFFERED_HISTORY_TYPES.map(historyType => option(HISTORY_TYPE_LABELS[historyType], historyType, effective === historyType))}
          </section>
        </v-box>
      </sl-dropdown>
      <fk-help>
        ${renderHistoryHelp(effective, project.timezone)}
      </fk-help>
    `;
  }

  private _select(historyType?: WireTypes.HistoryType) {
    this._dropdown?.hide();
    this.dispatchEvent(new FKReportHistorySelectEvent(historyType));
  }

  static styles = [linkStyles, css`
    :host {
      display: inline-flex;
      align-items: center;
      gap: var(--sl-spacing-2x-small);
    }

    fk-help {
      font-size: var(--sl-font-size-x-small);
    }

    .history-trigger {
      appearance: none;
      background: none;
      border: none;
      padding: 0;
      margin: 0;
      cursor: pointer;
      font: inherit;
      color: inherit;
      display: inline-flex;
      align-items: center;
      gap: var(--sl-spacing-2x-small);

      sl-icon {
        font-size: var(--sl-font-size-2x-small);
      }
    }

    .history-picker {
      min-width: 220px;
      background-color: white;
      padding: var(--sl-spacing-small) 0;
      border: 1px solid var(--fk-color-border);
      box-shadow: var(--fk-shadow-floating-small);
      border-radius: var(--sl-border-radius-large);
    }

    .history-options, .history-note {
      margin: 0 var(--sl-spacing-small);
    }

    .history-options {
      display: grid;
      grid-template-columns: auto 1fr;
      gap: var(--sl-spacing-2x-small) var(--sl-spacing-x-small);
    }

    .history-option {
      appearance: none;
      border: none;
      background: none;
      text-align: start;
      font-family: unset;
      font-size: unset;
      cursor: pointer;

      display: grid;
      grid-template-columns: subgrid;
      grid-column: 1/-1;
      align-items: center;
      padding: var(--sl-spacing-x-small);
      border-radius: var(--sl-border-radius-medium);

      &:hover {
        background-color: var(--sl-color-neutral-100);
      }
    }

    .history-note {
      max-width: 240px;
      font-size: var(--sl-font-size-small);
      color: var(--sl-color-neutral-500);
      white-space: wrap;
    }
  `];
}
